import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { siteInfo } from '../data/mock';
import { Phone, Mail, Instagram, MapPin, Send, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message envoyé avec succès ! Nous vous répondrons rapidement.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setGdprConsent(false);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Téléphone",
      value: siteInfo.phone,
      href: `tel:${siteInfo.phone}`
    },
    {
      icon: Mail,
      label: "Email",
      value: siteInfo.email,
      href: `mailto:${siteInfo.email}`
    },
    {
      icon: Instagram,
      label: "Instagram",
      value: siteInfo.instagram,
      href: "https://instagram.com"
    },
    {
      icon: MapPin,
      label: "Localisation",
      value: "Polynésie française & International",
      href: null
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#E8A5C4]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#C45B9F]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`text-center max-w-3xl mx-auto transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Contactez-nous
            </span>
            <h1 className="font-heading text-5xl lg:text-6xl text-[#3D2E35] mt-4">
              Parlons de votre <span className="font-script text-[#8B4B7C]">projet</span>
            </h1>
            <p className="text-lg text-[#6B5A62] mt-6">
              Nous serions ravis de donner vie à vos idées florales
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div
              className={`card-tevaite p-8 lg:p-10 transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <h2 className="font-heading text-2xl text-[#3D2E35] mb-6">
                Envoyez-nous un message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#6B5A62]">
                      Nom complet *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-[#E8A5C4]/30 focus:border-[#C45B9F] focus:ring-[#C45B9F]/20"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#6B5A62]">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-[#E8A5C4]/30 focus:border-[#C45B9F] focus:ring-[#C45B9F]/20"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#6B5A62]">
                      Téléphone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="border-[#E8A5C4]/30 focus:border-[#C45B9F] focus:ring-[#C45B9F]/20"
                      placeholder="+689 00 00 00 00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-[#6B5A62]">
                      Sujet *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="border-[#E8A5C4]/30 focus:border-[#C45B9F] focus:ring-[#C45B9F]/20"
                      placeholder="L'objet de votre message"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#6B5A62]">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="border-[#E8A5C4]/30 focus:border-[#C45B9F] focus:ring-[#C45B9F]/20 resize-none"
                    placeholder="Décrivez votre projet ou posez-nous vos questions..."
                  />
                </div>

                {/* GDPR Consent */}
                <div className="flex items-start space-x-3 p-4 bg-[#FAF8F5] rounded-xl">
                  <Checkbox
                    id="gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-1 border-[#C45B9F] data-[state=checked]:bg-[#8B4B7C]"
                  />
                  <div className="flex-1">
                    <Label htmlFor="gdpr" className="text-sm text-[#6B5A62] leading-relaxed cursor-pointer">
                      J'accepte que mes données personnelles soient utilisées pour répondre à ma demande. 
                      Consultez notre{' '}
                      <Link to="/politique-de-confidentialite" className="text-[#8B4B7C] hover:text-[#C45B9F] underline">
                        politique de confidentialité
                      </Link>
                      . *
                    </Label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !gdprConsent}
                  className="w-full btn-tevaite py-6 rounded-full text-white font-medium disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      Envoyer le message
                      <Send className="ml-2" size={20} />
                    </span>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div
              className={`space-y-8 transition-all duration-1000 delay-400 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <div>
                <h2 className="font-heading text-2xl text-[#3D2E35] mb-2">
                  Informations de contact
                </h2>
                <p className="text-[#6B5A62]">
                  N'hésitez pas à nous contacter directement
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-5 rounded-2xl bg-white border border-[#E8A5C4]/20 hover:border-[#C45B9F]/30 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E8A5C4] to-[#C45B9F] flex items-center justify-center flex-shrink-0">
                      <info.icon className="text-white" size={22} />
                    </div>
                    <div>
                      <p className="text-sm text-[#6B5A62] mb-1">{info.label}</p>
                      {info.href ? (
                        <a
                          href={info.href}
                          target={info.href.startsWith('http') ? '_blank' : undefined}
                          rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-[#3D2E35] font-medium hover:text-[#8B4B7C] transition-colors duration-300"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <span className="text-[#3D2E35] font-medium">{info.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Hours */}
              <div className="p-6 rounded-2xl bg-gradient-to-br from-[#E8A5C4]/20 to-[#C45B9F]/10 border border-[#E8A5C4]/30">
                <div className="flex items-center space-x-3 mb-4">
                  <Clock className="text-[#8B4B7C]" size={24} />
                  <h3 className="font-heading text-lg text-[#3D2E35]">
                    Horaires de contact
                  </h3>
                </div>
                <p className="text-[#6B5A62]">
                  Lundi - Vendredi : 8h - 18h (Heure de Tahiti)<br />
                  Samedi : 9h - 12h<br />
                  Dimanche : Fermé
                </p>
              </div>

              {/* International presence */}
              <div>
                <h3 className="font-heading text-lg text-[#3D2E35] mb-4">
                  Présence internationale
                </h3>
                <div className="flex flex-wrap gap-2">
                  {siteInfo.locations.map((loc, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-4 py-2 rounded-full bg-white text-[#6B5A62] text-sm border border-[#E8A5C4]/20"
                    >
                      <span className="mr-2">{loc.flag}</span>
                      {loc.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
